import type { Clasificacion, Documento, Expediente, Movimiento } from '../types'
import { fechaRel } from '../utils/fechas'
import { PERSONAL_ARCHIVO } from './abogados'

const CLASIFICACIONES: Clasificacion[] = ['Público', 'Privado', 'Copia certificada']

const PLANTILLA_DOCS: { tipo: string; descripcion: string; fojas: number }[] = [
  { tipo: 'Demanda', descripcion: 'Escrito inicial con anexos y acuse del juzgado', fojas: 34 },
  { tipo: 'Poder / carta poder', descripcion: 'Poder general para pleitos y cobranzas', fojas: 6 },
  {
    tipo: 'Identificación del cliente',
    descripcion: 'Identificación oficial del representante legal',
    fojas: 2,
  },
  { tipo: 'Acuerdo', descripcion: 'Acuerdo de admisión emitido por la autoridad', fojas: 4 },
  { tipo: 'Promoción', descripcion: 'Promoción de ofrecimiento de pruebas', fojas: 18 },
  {
    tipo: 'Prueba pericial',
    descripcion: 'Dictamen pericial rendido por el experto designado',
    fojas: 47,
  },
  { tipo: 'Acuse', descripcion: 'Acuse de notificación personal', fojas: 3 },
  { tipo: 'Oficio', descripcion: 'Oficio de contestación de la autoridad requerida', fojas: 9 },
  {
    tipo: 'Contrato de prestación de servicios',
    descripcion: 'Contrato base de la acción, copia certificada',
    fojas: 22,
  },
  { tipo: 'Factura', descripcion: 'Comprobante de pago de derechos y copias', fojas: 1 },
]

/** Documentos deterministas por expediente, para que cualquier detalle se vea poblado. */
export function documentosDe(exp: Expediente): Documento[] {
  const base = exp.localizador.charCodeAt(4) + exp.localizador.length
  const total = 5 + (base % 4)
  return Array.from({ length: total }).map((_, i) => {
    const plantilla = PLANTILLA_DOCS[(base + i * 3) % PLANTILLA_DOCS.length]
    const dias = -(140 - i * 17 - (base % 9))
    const conAcuse = i % 3 !== 2
    const fecha = fechaRel(Math.min(dias, -2))
    const persona = PERSONAL_ARCHIVO[(base + i) % PERSONAL_ARCHIVO.length]
    const archivo = `${exp.localizador}-${String(i + 1).padStart(2, '0')}.pdf`
    const fechaHora = `${fecha} 1${i % 2}:${i === 1 ? '45' : '20'}`
    const fojas = plantilla.fojas + (i % 5)
    return {
      id: `${exp.id}-doc-${i + 1}`,
      expedienteId: exp.id,
      tipo: plantilla.tipo,
      descripcion: plantilla.descripcion,
      fecha,
      fechaAcuse: conAcuse ? fechaRel(Math.min(dias + 2, -1)) : undefined,
      fojas,
      clasificacion: CLASIFICACIONES[(base + i) % 3],
      archivo,
      ubicacion: `${exp.ubicacion.caja} · Legajo ${(i % 3) + 1}`,
      esEvidencia: false,
      origen: 'Escáner',
      paginas: Array.from({ length: 1 + (i % 3) }).map((__, p) => ({
        id: `${exp.id}-doc-${i + 1}-p${p + 1}`,
        nombre: `${archivo.replace('.pdf', '')}_p${p + 1}.pdf`,
        rotacion: 0,
        origen: 'Escáner' as const,
      })),
      subidoPor: persona,
      subidoEn: fechaHora,
      versiones: [
        {
          version: 1,
          archivo,
          usuario: persona,
          fechaHora,
          nota: 'Digitalización del documento físico al integrarse al expediente.',
        },
      ],
    }
  })
}

/** Bitácora de préstamos del expediente físico, estilo entradas y salidas de almacén. */
export function bitacoraDe(exp: Expediente): Movimiento[] {
  const base = exp.localizador.length + exp.cliente.length
  const personas = [
    ...PERSONAL_ARCHIVO,
    'Lic. Mariana Cervantes Rojo',
    'Lic. Ricardo Menchaca Villalobos',
    'Lic. Ana Paula Zúñiga Bernal',
  ]
  const motivos = [
    'Preparación de audiencia',
    'Copias certificadas para el juzgado',
    'Revisión con el cliente',
    'Integración de nuevas promociones',
    'Digitalización de documentos',
  ]

  const movimientos: Movimiento[] = []
  for (let i = 0; i < 3; i++) {
    const salida = -(60 - i * 18 + (base % 5))
    const persona = personas[(base + i) % personas.length]
    const motivo = motivos[(base + i * 2) % motivos.length]
    movimientos.push({
      id: `${exp.id}-mov-s${i}`,
      expedienteId: exp.id,
      tipo: 'Salida',
      persona,
      fecha: fechaRel(salida),
      hora: `0${8 + (i % 2)}:${i === 1 ? '45' : '20'}`,
      motivo,
    })
    movimientos.push({
      id: `${exp.id}-mov-e${i}`,
      expedienteId: exp.id,
      tipo: 'Entrada',
      persona,
      fecha: fechaRel(salida + 2),
      hora: `1${6 + (i % 2)}:${i === 2 ? '05' : '35'}`,
      motivo: `Devolución · ${motivo.toLowerCase()}`,
    })
  }

  if (exp.prestadoA && exp.prestadoDesde) {
    movimientos.push({
      id: `${exp.id}-mov-abierto`,
      expedienteId: exp.id,
      tipo: 'Salida',
      persona: exp.prestadoA,
      fecha: exp.prestadoDesde,
      hora: '09:10',
      motivo: 'Préstamo vigente · sin devolución registrada',
    })
  }

  return movimientos.sort((a, b) => (a.fecha < b.fecha ? 1 : -1))
}

